import React, { useState, useRef } from "react"
import types from "prop-types"
import Formsy from "formsy-react"
import { isNil } from "lodash-es"
import { useDispatch, useSelector } from "react-redux"
import { PUBLIC_CONTEXT, addMessageToConversation, context } from "reduxSlices/formSubmissionConversationsSlice"
import { submitterFullName as submitterFullNameSelector } from "reduxSlices/formSubmissionSlice"
import { PrimaryButton } from "shared/buttons"
import { FormsyMultilineTextInput } from "shared/inputs"

const ConversationMessageForm = ({ conversationId }) => {
  const formIdentifier = `message-form-for-conversation-${conversationId}`

  const dispatch = useDispatch()

  const formRef = useRef()

  const conversationContext = useSelector(context)
  const submitterName = useSelector(submitterFullNameSelector)

  const [formIsValid, setFormIsValid] = useState(false)
  const disableSubmit = () => setFormIsValid(false)
  const enableSubmit = () => setFormIsValid(true)

  const resetForm = () => formRef.current?.reset()

  const createMessage = async (messageData) => {
    try {
      await dispatch(addMessageToConversation({ conversationId, messageData })).unwrap()
      resetForm()
    } catch {
      // Error state handled by redux slice automatically when the thunk is rejected.
    }
  }

  return (
    <Formsy
      id={formIdentifier}
      onInvalid={disableSubmit}
      onSubmit={createMessage}
      onValid={enableSubmit}
      ref={formRef}
    >
      <div className="flex flex-col lg:flex-row lg:items-center gap-4 w-full">
        <div className="grow w-full flex flex-col gap-4">
          <div>
            <FormsyMultilineTextInput
              className="bordered-base-input overflow-y-auto thin-scrollbar transparent-scroll-track"
              name="text"
              placeholder="Message ..."
              required
              validations={{
                isNotBlankString: true,
                noExcelMetacharacters: true,
                noHTML: true,
              }}
              validationErrors={{ isDefaultRequiredValue: "required" }}
            />
          </div>
          {
            formIsValid && (
              <div className="flex flex-col gap-4 text-xs text-dark">
                {
                  conversationContext === PUBLIC_CONTEXT
                    ? (
                      <p>
                        This message will be part of this submission&apos;s record and
                        visible to anyone with access to the submission.
                      </p>
                    )
                    : (
                      <p>
                        This message will be accessible to the submitter
                        {isNil(submitterName) ? "" : `, ${submitterName},`} until the submission is closed.
                        It will be permanently accessible to any user with access to this submission.
                      </p>
                    )
                }
                <p>
                  Messages cannot be deleted or edited.
                </p>
              </div>
            )
          }
        </div>
        <PrimaryButton
          ariaLabel="submit message form"
          className="w-full lg:w-auto lg:shrink-0"
          disabled={!formIsValid}
          form={formIdentifier}
          text="Add Message"
          type="submit"
        />
      </div>
    </Formsy>
  )
}

ConversationMessageForm.propTypes = {
  conversationId: types.number.isRequired,
}

export default ConversationMessageForm
